import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Modal } from '@skupreme/shared/ui';
import {
  CountryDropdownFormField,
  ControlledPhoneFormField,
  ControlledTextFormField,
} from '@elements/FormField';
import GoogleAutoComplete from '@elements/GoogleAutocomplete';
import { marketplaceRegionHook } from '@hooks/marketplace-region';
import { dropdownOptionsIncludeFlag } from '@elements/Flag';
import type { ParsedPastedAddress } from '@utility/address-parser';

/* The message is the one yup generated from the field path. */
const addressSchema = z
  .object({
    name: z
      .string({ required_error: 'name is a required field' })
      .min(1, 'name is a required field'),
    company: z.string().optional(),
    address_line_one: z.string().optional(),
    address_line_two: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    country: z.string().optional(),
    phone: z.string().optional(),
    zip_code: z.string().optional(),
  })
  .passthrough();

type PastedAddressForm = z.infer<typeof addressSchema>;
type AddressField = keyof typeof addressSchema.shape;

/* Written to the form even when blank, so a new paste clears the last one's. */
const ALWAYS_SET: string[] = [
  'address_line_one',
  'city',
  'state',
  'country',
  'zip_code',
];

type PasteAddressPopupProps = {
  open: boolean;
  /** Called with nothing: the popup is done, whichever way it closed. */
  closeModal: (result?: unknown) => void;
  extractAddress: (address: PastedAddressForm) => void;
  defaultName?: string;
};

export function PasteAddressPPopup({
  open,
  closeModal,
  extractAddress,
  defaultName,
}: PasteAddressPopupProps) {
  const formId = useId();
  const { data: regionData, loading: regionLoading } = marketplaceRegionHook();

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<PastedAddressForm>({
    defaultValues: {
      name: defaultName || '',
    },
    resolver: zodResolver(addressSchema),
    shouldUnregister: true,
  });

  const [formatedAddress, setFormattedAddress] = useState<
    Partial<ParsedPastedAddress>
  >({});
  const [unsupportedCountry, setUnsupportedCountry] = useState(false);

  useEffect(() => {
    setUnsupportedCountry(false);
    const address = { ...formatedAddress };
    const region = regionData.find(element => address.country === element.name);
    if (region) {
      address.country = region.value;
    } else if (Object.keys(address).length > 0) {
      setUnsupportedCountry(true);
    }
    for (const [key, value] of Object.entries(address)) {
      if (value || ALWAYS_SET.includes(key)) {
        setValue(key as AddressField, String(value));
      }
    }
  }, [formatedAddress]);

  const onSubmit = (data: PastedAddressForm) => {
    extractAddress({
      ...data,
      phone: data.phone?.replace?.(/[()]/g, ''),
    });
    closeModal();
  };

  return (
    <Modal
      isOpen={open}
      onOpenChange={isOpen => !isOpen && closeModal()}
      title="Manual Order"
      size="xl"
      showCloseButton
      footer={
        <>
          <Button onPress={() => closeModal()}>Cancel</Button>
          <Button variant="primary" type="submit" form={formId}>
            Use this address
          </Button>
        </>
      }
    >
      <form
        id={formId}
        autoComplete="skupreme-off"
        onSubmit={handleSubmit(onSubmit)}
        className="sk:grid sk:gap-4 sk:md:grid-cols-2"
      >
        <div>
          <GoogleAutoComplete
            setFormattedAddress={setFormattedAddress}
            name="searchAddress"
            aria-label="Address to paste"
            placeholder="Paste or type an address here"
            autoComplete="skupreme-off"
            className="sk:mt-5"
          />
          {unsupportedCountry && (
            <div className="sk:mt-2 sk:text-danger">
              This country is not supported
            </div>
          )}
        </div>
        <div>
          <ControlledTextFormField
            error={errors.name}
            label="Name"
            name="name"
            control={control}
            required
            autoComplete="skupreme-off"
            defaultValue={defaultName}
          />
          <ControlledTextFormField
            label="Company"
            name="company"
            control={control}
            autoComplete="skupreme-off"
          />
          <ControlledPhoneFormField
            control={control}
            name="phone"
            label="Phone Number"
          />
          <ControlledTextFormField
            error={errors.address_line_one}
            label="Address"
            name="address_line_one"
            control={control}
            autoComplete="skupreme-off"
          />
          <ControlledTextFormField
            error={errors.address_line_two}
            name="address_line_two"
            aria-label="Address line 2"
            control={control}
            autoComplete="skupreme-off"
          />
          <div className="sk:grid sk:grid-cols-2 sk:gap-3">
            <ControlledTextFormField
              error={errors.city}
              label="City"
              name="city"
              autoComplete="skupreme-off"
              control={control}
            />
            <ControlledTextFormField
              error={errors.state}
              label="State"
              name="state"
              autoComplete="skupreme-off"
              control={control}
            />
          </div>
          <div className="sk:grid sk:grid-cols-2 sk:gap-3">
            <ControlledTextFormField
              error={errors.zip_code}
              label="Zip"
              name="zip_code"
              control={control}
              autoComplete="skupreme-off"
            />
            {/* @ts-expect-error - TS2740: Type '{ options: { image: { src: string; }; flag: null; key?... */}
            <CountryDropdownFormField
              options={dropdownOptionsIncludeFlag(regionData)}
              loading={regionLoading}
              name="country"
              label="Country"
              control={control}
              defaultValue=""
              autoComplete="skupreme-off"
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}
